import React, { useEffect } from 'react';

const LeaderFollowerArchitecture = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Leader-Follower Architecture in Distributed Systems - Complete Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master leader-follower architecture patterns for distributed systems. Learn leader election, consensus algorithms, and consistency management.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master leader-follower architecture patterns for distributed systems. Learn leader election, consensus algorithms, and consistency management.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Leader-Follower Architecture in Distributed Systems</h1>
        <p style={styles.subtitle}>
          Manage consistency in writes and coordinate distributed operations with leader election
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is Leader-Follower Architecture?</h2>
          <p style={styles.paragraph}>
            Leader-follower architecture is a distributed system pattern where one node (the leader) coordinates 
            operations and makes decisions, while other nodes (followers) execute those decisions and provide redundancy. 
            This pattern ensures consistency in distributed operations and provides a clear coordination mechanism.
          </p>
          <p style={styles.paragraph}>
            The leader is responsible for accepting client requests, making decisions about data changes, and 
            coordinating with followers to ensure consistency across the system. If the leader fails, followers 
            can elect a new leader to maintain system availability.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Core Components</h2>
          
          <h3 style={styles.heading}>Leader Responsibilities</h3>
          <p style={styles.paragraph}>
            The leader node has several critical responsibilities in maintaining system consistency and coordination:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Request Processing:</strong> Accept and process client write requests
            </li>
            <li style={styles.listItem}>
              <strong>Decision Making:</strong> Make authoritative decisions about data changes and system state
            </li>
            <li style={styles.listItem}>
              <strong>Coordination:</strong> Coordinate with followers to replicate changes and maintain consistency
            </li>
            <li style={styles.listItem}>
              <strong>Heartbeat:</strong> Send regular heartbeat messages to followers to maintain leadership
            </li>
          </ul>

          <h3 style={styles.heading}>Follower Responsibilities</h3>
          <p style={styles.paragraph}>
            Follower nodes support the leader and provide system redundancy:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Replication:</strong> Apply changes received from the leader to maintain data consistency
            </li>
            <li style={styles.listItem}>
              <strong>Read Serving:</strong> Serve read requests to distribute load (depending on consistency requirements)
            </li>
            <li style={styles.listItem}>
              <strong>Leader Monitoring:</strong> Monitor leader health and participate in leader election when needed
            </li>
            <li style={styles.listItem}>
              <strong>Backup:</strong> Maintain up-to-date copies of data to enable quick leadership transition
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Leader Election Algorithms</h2>
          
          <h3 style={styles.heading}>Bully Algorithm</h3>
          <p style={styles.paragraph}>
            The Bully Algorithm is a simple leader election algorithm where the node with the highest ID becomes 
            the leader. When a node detects leader failure, it initiates an election process.
          </p>
          <pre style={styles.codeBlock}>
{`Bully Algorithm Process:

1. Node detects leader failure
2. Node sends ELECTION message to all higher-ID nodes
3. If no response from higher-ID nodes:
   - Node declares itself leader
   - Sends COORDINATOR message to all nodes
4. If response received from higher-ID node:
   - Wait for COORDINATOR message
   - If timeout, restart election

Example:
Nodes: [1, 2, 3, 4, 5] (5 is current leader)
Leader 5 fails:
- Node 4 detects failure
- Node 4 sends ELECTION to node 5 (no response)
- Node 4 becomes leader
- Node 4 sends COORDINATOR to [1, 2, 3]`}
          </pre>

          <h3 style={styles.heading}>Raft Consensus Algorithm</h3>
          <p style={styles.paragraph}>
            Raft is a more sophisticated consensus algorithm that ensures safety and handles network partitions 
            gracefully. It uses terms and voting to elect leaders and maintain consistency.
          </p>
          <pre style={styles.codeBlock}>
{`Raft Leader Election:

States: Follower, Candidate, Leader

Election Process:
1. Follower timeout → becomes Candidate
2. Candidate increments term, votes for self
3. Candidate requests votes from other nodes
4. If majority votes received → becomes Leader
5. If another leader discovered → becomes Follower
6. If election timeout → start new election

Key Properties:
- Election Safety: At most one leader per term
- Leader Append-Only: Leader never overwrites entries
- Log Matching: Logs are consistent across nodes
- Leader Completeness: Leader has all committed entries`}
          </pre>

          <h3 style={styles.heading}>Ring-Based Election</h3>
          <p style={styles.paragraph}>
            In ring-based election, nodes are arranged in a logical ring, and election messages are passed around 
            the ring until a leader is chosen.
          </p>
          <pre style={styles.codeBlock}>
{`Ring Election Process:

1. Node detects leader failure
2. Node starts election by sending message with its ID
3. Message travels around ring, each node adds its ID
4. When message returns to initiator:
   - Node with highest ID becomes leader
   - ELECTED message sent around ring

Advantages:
✓ Guaranteed to terminate
✓ Works with any number of failures
✓ Simple to implement

Disadvantages:
✗ Can be slow in large rings
✗ Vulnerable to ring partitions`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Implementation Example</h2>
          <p style={styles.paragraph}>
            Here's a simplified implementation of a leader-follower system with basic leader election:
          </p>
          <pre style={styles.codeBlock}>
{`import time
import threading
from enum import Enum
from typing import List, Optional

class NodeState(Enum):
    FOLLOWER = "follower"
    CANDIDATE = "candidate"
    LEADER = "leader"

class Node:
    def __init__(self, node_id: int, cluster_nodes: List[int]):
        self.node_id = node_id
        self.cluster_nodes = cluster_nodes
        self.state = NodeState.FOLLOWER
        self.current_term = 0
        self.voted_for = None
        self.leader_id = None
        self.last_heartbeat = time.time()
        self.election_timeout = 5.0  # seconds
        self.heartbeat_interval = 2.0  # seconds
        
        # Start monitoring thread
        threading.Thread(target=self._monitor_leader, daemon=True).start()
    
    def _monitor_leader(self):
        """Monitor leader heartbeats and trigger elections"""
        while True:
            time.sleep(1)
            
            if self.state == NodeState.FOLLOWER:
                if time.time() - self.last_heartbeat > self.election_timeout:
                    self._start_election()
            elif self.state == NodeState.LEADER:
                self._send_heartbeat()
    
    def _start_election(self):
        """Start leader election process"""
        print(f"Node {self.node_id}: Starting election for term {self.current_term + 1}")
        
        self.state = NodeState.CANDIDATE
        self.current_term += 1
        self.voted_for = self.node_id
        votes_received = 1  # Vote for self
        
        # Request votes from other nodes
        for node_id in self.cluster_nodes:
            if node_id != self.node_id:
                if self._request_vote(node_id):
                    votes_received += 1
        
        # Check if majority achieved
        if votes_received > len(self.cluster_nodes) // 2:
            self._become_leader()
        else:
            self._become_follower()
    
    def _request_vote(self, node_id: int) -> bool:
        """Request vote from another node"""
        # Simplified - in real implementation, this would be network call
        print(f"Node {self.node_id}: Requesting vote from node {node_id}")
        # Simulate vote response based on term comparison
        return True  # Simplified for example
    
    def _become_leader(self):
        """Transition to leader state"""
        print(f"Node {self.node_id}: Became leader for term {self.current_term}")
        self.state = NodeState.LEADER
        self.leader_id = self.node_id
        self._send_heartbeat()
    
    def _become_follower(self):
        """Transition to follower state"""
        print(f"Node {self.node_id}: Became follower for term {self.current_term}")
        self.state = NodeState.FOLLOWER
        self.voted_for = None
    
    def _send_heartbeat(self):
        """Send heartbeat to all followers"""
        if self.state == NodeState.LEADER:
            print(f"Node {self.node_id}: Sending heartbeat")
            # In real implementation, send to all followers
            time.sleep(self.heartbeat_interval)
    
    def receive_heartbeat(self, leader_id: int, term: int):
        """Receive heartbeat from leader"""
        if term >= self.current_term:
            self.current_term = term
            self.leader_id = leader_id
            self.last_heartbeat = time.time()
            
            if self.state != NodeState.FOLLOWER:
                self._become_follower()
    
    def process_write_request(self, data):
        """Process write request (only leader can handle writes)"""
        if self.state != NodeState.LEADER:
            return {"error": "Not leader", "leader_id": self.leader_id}
        
        # Process write and replicate to followers
        print(f"Leader {self.node_id}: Processing write: {data}")
        self._replicate_to_followers(data)
        return {"success": True, "leader_id": self.node_id}
    
    def _replicate_to_followers(self, data):
        """Replicate data to all followers"""
        for node_id in self.cluster_nodes:
            if node_id != self.node_id:
                print(f"Leader {self.node_id}: Replicating to follower {node_id}")
                # In real implementation, send data to follower

# Usage Example
cluster = [1, 2, 3, 4, 5]
nodes = [Node(i, cluster) for i in cluster]

# Simulate leader failure
time.sleep(10)
print("Simulating leader failure...")
# Leader election will be triggered automatically`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Consistency Models</h2>
          
          <h3 style={styles.heading}>Strong Consistency</h3>
          <p style={styles.paragraph}>
            In strong consistency mode, all reads are served by the leader to ensure clients always see the most 
            recent writes. This provides the strongest consistency guarantees but limits read scalability.
          </p>
          <pre style={styles.codeBlock}>
{`Strong Consistency Implementation:

class StrongConsistencyCluster:
    def read(self, key):
        # Always read from leader
        if self.state != NodeState.LEADER:
            return self._forward_to_leader("READ", key)
        
        return self.data.get(key)
    
    def write(self, key, value):
        # Only leader accepts writes
        if self.state != NodeState.LEADER:
            return self._forward_to_leader("WRITE", key, value)
        
        # Write to leader and replicate synchronously
        self.data[key] = value
        self._replicate_synchronously(key, value)
        return {"success": True}

Guarantees:
✓ Read-your-writes consistency
✓ Monotonic read consistency
✓ Strong consistency across all clients

Trade-offs:
✗ Higher read latency
✗ Leader becomes read bottleneck
✗ Reduced availability during leader election`}
          </pre>

          <h3 style={styles.heading}>Eventual Consistency</h3>
          <p style={styles.paragraph}>
            Eventual consistency allows followers to serve reads, providing better performance and availability 
            at the cost of potentially serving stale data.
          </p>
          <pre style={styles.codeBlock}>
{`Eventual Consistency Implementation:

class EventualConsistencyCluster:
    def read(self, key):
        # Followers can serve reads
        return self.data.get(key)
    
    def write(self, key, value):
        # Only leader accepts writes
        if self.state != NodeState.LEADER:
            return self._forward_to_leader("WRITE", key, value)
        
        # Write to leader and replicate asynchronously
        self.data[key] = value
        self._replicate_asynchronously(key, value)
        return {"success": True}
    
    def _replicate_asynchronously(self, key, value):
        # Replicate in background
        threading.Thread(
            target=self._send_to_followers,
            args=(key, value),
            daemon=True
        ).start()

Benefits:
✓ Lower read latency
✓ Better read scalability
✓ Higher availability

Trade-offs:
✗ Potential for stale reads
✗ Read-your-writes not guaranteed
✗ Temporary inconsistency`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Handling Network Partitions</h2>
          
          <h3 style={styles.heading}>Split-Brain Prevention</h3>
          <p style={styles.paragraph}>
            Split-brain occurs when network partitions cause multiple nodes to believe they are the leader. 
            This can lead to data inconsistency and conflicts.
          </p>
          <pre style={styles.codeBlock}>
{`Split-Brain Prevention Strategies:

1. Quorum-Based Leadership:
   - Leader must maintain connection to majority of nodes
   - If leader loses majority, it steps down
   - Only partition with majority can elect new leader

2. Witness/Arbiter Nodes:
   - Use odd number of nodes (3, 5, 7)
   - Include lightweight witness nodes for voting
   - Witness nodes don't store data, only participate in elections

3. Lease-Based Leadership:
   - Leader must renew lease periodically
   - Lease expires if leader can't reach majority
   - New leader can only be elected after lease expiry

4. External Coordination Service:
   - Use external service (ZooKeeper, etcd) for coordination
   - Leader registration with TTL
   - Atomic leader election through external service`}
          </pre>

          <h3 style={styles.heading}>Partition Tolerance Implementation</h3>
          <p style={styles.paragraph}>
            Implement mechanisms to handle network partitions gracefully:
          </p>
          <pre style={styles.codeBlock}>
{`class PartitionTolerantNode:
    def __init__(self, node_id, cluster_nodes):
        self.node_id = node_id
        self.cluster_nodes = cluster_nodes
        self.connected_nodes = set(cluster_nodes)
        self.lease_expiry = None
        self.lease_duration = 30  # seconds
    
    def check_partition_status(self):
        """Check if node is in majority partition"""
        majority_size = len(self.cluster_nodes) // 2 + 1
        return len(self.connected_nodes) >= majority_size
    
    def handle_node_disconnection(self, node_id):
        """Handle disconnection from another node"""
        self.connected_nodes.discard(node_id)
        
        if self.state == NodeState.LEADER:
            if not self.check_partition_status():
                print(f"Leader {self.node_id}: Lost majority, stepping down")
                self._step_down()
    
    def _step_down(self):
        """Step down from leadership"""
        self.state = NodeState.FOLLOWER
        self.leader_id = None
        print(f"Node {self.node_id}: Stepped down from leadership")
    
    def _acquire_leadership_lease(self):
        """Acquire leadership lease"""
        if self.check_partition_status():
            self.lease_expiry = time.time() + self.lease_duration
            return True
        return False
    
    def _renew_lease(self):
        """Renew leadership lease"""
        if self.state == NodeState.LEADER:
            if self.check_partition_status():
                self.lease_expiry = time.time() + self.lease_duration
                return True
            else:
                self._step_down()
                return False
        return False`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Examples</h2>
          
          <h3 style={styles.heading}>Apache Kafka</h3>
          <p style={styles.paragraph}>
            Kafka uses leader-follower architecture for each partition. One broker acts as the leader for a partition, 
            handling all reads and writes, while other brokers act as followers, replicating data for fault tolerance.
          </p>

          <h3 style={styles.heading}>MongoDB Replica Sets</h3>
          <p style={styles.paragraph}>
            MongoDB uses replica sets with one primary (leader) and multiple secondaries (followers). The primary 
            handles all writes, while secondaries can serve reads depending on read preference settings.
          </p>

          <h3 style={styles.heading}>Elasticsearch Clusters</h3>
          <p style={styles.paragraph}>
            Elasticsearch elects a master node (leader) responsible for cluster-wide operations like creating/deleting 
            indices and managing cluster topology, while data nodes (followers) handle data storage and search operations.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Use odd number of nodes:</strong> Deploy clusters with odd numbers (3, 5, 7) to avoid 
              split-brain scenarios and ensure clear majorities.
            </li>
            <li style={styles.listItem}>
              <strong>Implement proper timeouts:</strong> Set appropriate election timeouts and heartbeat intervals 
              based on network characteristics and failure detection requirements.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor leader health:</strong> Continuously monitor leader health and performance to detect 
              issues before they cause failures.
            </li>
            <li style={styles.listItem}>
              <strong>Test failover scenarios:</strong> Regularly test leader election and failover procedures 
              to ensure they work correctly under various failure conditions.
            </li>
            <li style={styles.listItem}>
              <strong>Consider consistency requirements:</strong> Choose between strong and eventual consistency 
              based on your application's requirements for data accuracy vs. performance.
            </li>
            <li style={styles.listItem}>
              <strong>Implement graceful degradation:</strong> Design followers to handle read-only operations 
              during leader elections to maintain partial availability.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Leader-follower architecture provides a robust pattern for coordinating distributed systems and ensuring 
            consistency. The key is choosing the right leader election algorithm and consistency model for your 
            specific requirements.
          </p>
          <p style={styles.paragraph}>
            Remember that leader-follower systems involve trade-offs between consistency, availability, and partition 
            tolerance. Design your system to handle leader failures gracefully, prevent split-brain scenarios, and 
            provide appropriate consistency guarantees for your use case.
          </p>
        </section>
      </main>
    </article>
  );
};

export default LeaderFollowerArchitecture;
